/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 ~ Copyright 2020 Adobe Systems Incorporated
 ~
 ~ Licensed under the Apache License, Version 2.0 (the "License");
 ~ you may not use this file except in compliance with the License.
 ~ You may obtain a copy of the License at
 ~
 ~     http://www.apache.org/licenses/LICENSE-2.0
 ~
 ~ Unless required by applicable law or agreed to in writing, software
 ~ distributed under the License is distributed on an "AS IS" BASIS,
 ~ WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 ~ See the License for the specific language governing permissions and
 ~ limitations under the License.
 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

import React from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager'
import {
  Text as UIFoundationsText,
  TextHtmlTags,
  TextHyphens
} from '@dcxwam/dcx-wam-ui-foundations'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../functional/ErrorBoundary'
import {
  getTextAlignmentMappedProp,
  getMarginBottomMappedProp,
  getTextVariant
} from '../../../utils/props-mappings'
import TextEditConfig from './TextEditConfig'
import { TextModelProps } from './types'
import { EditableComponentPropsType } from '../../../utils/prop-types'
import { useDataMarkerReplace } from '../../../utils/data-marker-replace'
import withComponentRefresh from '../../../utils/aem/editor/withComponentRefresh'
import {
  WithAutomaticTestIdsType,
  withAutomaticTestIds
} from '../../../utils/aem/with-automatic-test-ids'
import { useExtractEditableProps } from '../../../utils/aem/editor/extractEditableProps'

const Text = withComponentRefresh(
  withAutomaticTestIds(
    (
      props: WithAutomaticTestIdsType<
        EditableComponentPropsType<TextModelProps>
      >
    ) => {
      const {
        text,
        richText,
        textAlign,
        hyphenation,
        paraFormat,
        textVariant,
        marginBottom,
        theme,
        id,
        ...rest
      } = props.model
      const { getDataTestIdObject } = props
      const editableProps = useExtractEditableProps(props, rest)

      const markerReplacedData = useDataMarkerReplace(
        { text: text || '' },
        text || '',
        AuthoringUtils.isInEditor()
      )

      let component = TextHtmlTags.P
      switch (paraFormat) {
        case 'p':
          component = TextHtmlTags.P
          break
        case 'h1':
          component = TextHtmlTags.H1
          break
        case 'h2':
          component = TextHtmlTags.H2
          break
        case 'h3':
          component = TextHtmlTags.H3
          break
        case 'h4':
          component = TextHtmlTags.H4
          break
        case 'h5':
          component = TextHtmlTags.H5
          break
        case 'h6':
          component = TextHtmlTags.H6
          break
        default:
          break
      }
      const hyphens = hyphenation ? TextHyphens.Auto : TextHyphens.None
      const variant = getTextVariant(textVariant)
      const alignment = getTextAlignmentMappedProp(textAlign)
      const configAtom = {
        content: markerReplacedData?.text || text,
        isHtml: richText,
        component,
        variant,
        hyphens,
        textTheme: theme,
        horizontalAlignment: alignment,
        id,
        ...getDataTestIdObject('text', id)
      }

      const configSection = {
        hasMarginBottom: getMarginBottomMappedProp(marginBottom),
        textHorizontalAlignment: alignment
      }

      return (
        <EditableComponent {...editableProps}>
          {text ? (
            <UIFoundationsText.TextSection {...configSection}>
              <UIFoundationsText {...configAtom} />
            </UIFoundationsText.TextSection>
          ) : (
            TextEditConfig.emptyLabel
          )}
        </EditableComponent>
      )
    }
  )
)

const TextWithErrorBoundary = withErrorBoundary(Text, {
  componentName: 'Text',
  fallback: <ErrorFallback componentName="Text" />
})

export default TextWithErrorBoundary
